import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { Divider, Upload } from 'antd';
import { isEmpty, orderBy, uniqBy } from 'lodash';
import { type MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCSVDownloader } from 'react-papaparse';
import styled from 'styled-components';

import { useUploadContext } from 'components/FileUploader';
import { Button, Collapse, useMessage } from 'components/ui';
import {
  type AtlasGqlValidateImageMetadataFileInput,
  AtlasGqlDecimalSeparator,
  AtlasGqlFileFieldDelimiter,
  AtlasGqlFileFormat,
  useGetImageMetadataFieldsQuery,
  useValidateImageMetadataFileMutation,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import {
  AUTONOMOUS_DRONE,
  BLADE_INTERNAL,
  CSV_BULK_UPLOAD_OPTIONS,
  IMAGE_AND_METADATA_UPLOAD_OPTIONS,
  JSON_BULK_UPLOAD_OPTIONS,
  MANUAL_DRONE,
} from 'utils/constants';
import { uploadIngestFiles, useGetIngestUploadUrls } from 'utils/ingest';
import { AERO_ENTERPRISE_UPLOAD, SKYCRAWLER_UPLOAD } from 'utils/release-toggles';

import { getBulkUploadEventTypes } from '../../utils';
import { type ExampleImageDetails, EXAMPLE_IMAGE_DETAILS } from '../constants';
import { useUploadInspectionDataModalContext } from '../Context/UploadInspectionDataModalContext';
import { FieldMappingForm } from '../FieldMappingForm';
import {
  type CloseOnSuccessProps,
  type InspectionUploadWorkflowComponentProps,
  Steps,
  UploadTypes,
} from '../UploadInspectionDataModal';
import { ButtonContainer } from '../UploadInspectionDataModal.style';
import { flatParseJSON, getProvidedFieldNamesFromJsonRecords, parseCsv } from '../utils';

import { AeroEnterpriseUpload } from './AeroEnterpriseUpload';
import { Preview } from './Preview';
import { RoboticsUpload } from './RoboticsUpload';
import { shouldShowSkydioImport, SkydioImportPanel } from './SkydioImportPanel';
import { type ImageDetailsValidationProps, Validation } from './Validation';

type ImageDetailsProps = Omit<
  InspectionUploadWorkflowComponentProps,
  'setValidationStepProps' | 'setCreatedInspectionIds' | 'setCreatedTaskIds'
> & {
  initialStep?: Steps;
  inspectionIds: string[];
  uploadType: UploadTypes;
  onCloseModal: () => void;
  onProcessComplete: (input: CloseOnSuccessProps) => void;
  validateImageMetadataInputRef: MutableRefObject<
    AtlasGqlValidateImageMetadataFileInput | undefined
  >;
  canUseSkydioImport?: boolean;
};

export const ImageDetails = ({
  requireTaskId,
  step,
  setStep,
  initialStep,
  inspectionIds,
  inspectionTasks,
  uploadType,
  uploadId,
  onCloseModal,
  onProcessComplete,
  onComplete,
  validateImageMetadataInputRef,
  canUseSkydioImport = false,
}: ImageDetailsProps) => {
  const { t } = useTranslation();
  const { hasReleaseToggle, features } = useAccountContext();
  const hasSkyCrawlerUploadRT = hasReleaseToggle(SKYCRAWLER_UPLOAD);
  const hasAeroEnterpriseUploadRT = hasReleaseToggle(AERO_ENTERPRISE_UPLOAD);
  const {
    SKYCRAWLER_UPLOAD: hasSkyCrawlerUploadFlag,
    AERO_ENTERPRISE_UPLOAD: hasAeroEnterpriseUploadFlag,
    ROBOTICS_UPLOAD: hasRoboticsUploadFlag,
  } = features;

  const { setAlertState, setErroredUploadSteps, imageDetailsContext, inspectionType } =
    useUploadInspectionDataModalContext();
  const {
    file,
    setFile,
    fieldMappings,
    setFieldMappings,
    previewData,
    setPreviewData,
    setImagePathFieldName,
    imageDetailsFileType: fileType,
    setImageDetailsFileType: setFileType,
  } = imageDetailsContext;

  const [providedFieldNames, setProvidedFieldNames] = useState<string[]>();
  const [inspectionUploadEventTypes, setInspectionUploadEventTypes] = useState(() =>
    getBulkUploadEventTypes(CSV_BULK_UPLOAD_OPTIONS.CSV_BULK)
  );
  const [likelyDelimiterError, setLikelyDelimiterError] = useState<boolean>(false);
  const [cellDelimiter, setCellDelimiter] = useState<AtlasGqlFileFieldDelimiter>();
  const [decimalDelimiter, setDecimalDelimiter] = useState<AtlasGqlDecimalSeparator>(
    AtlasGqlDecimalSeparator.Period
  );
  const [validationStepProps, setValidationStepProps] = useState<ImageDetailsValidationProps>();

  /**
   * TODO: SC-125542
   *   - state for validation step
   *   - call props.setErroredUploadSteps(uploadErrors => ({...uploadErrors, imageDetails: TODO}))
   *     as appropriate if validation has errors
   */

  const { message } = useMessage();

  const { data: imageMetadataFieldsData } = useGetImageMetadataFieldsQuery({
    variables: inspectionType?.id ? { inspectionTypeId: inspectionType.id } : undefined,
  });
  const imageMetadataFields = orderBy(
    imageMetadataFieldsData?.imageMetadataFields ?? [],
    'defaultOrder'
  );

  const internalFields = useMemo(() => {
    const fields = uniqBy(imageMetadataFields, 'internalName');

    if (requireTaskId) {
      return fields.map(field =>
        field.internalName === 'taskId' ? { ...field, required: true } : field
      );
    }

    return fields;
  }, [imageMetadataFields, requireTaskId]);

  const existingTaskCount = inspectionTasks?.length ?? 0;

  const { exampleCsv, exampleJson } = useMemo(() => {
    if (internalFields.length === 0) {
      return { exampleCsv: '', exampleJson: '' };
    }

    let exampleImageDetails: ExampleImageDetails[] = EXAMPLE_IMAGE_DETAILS;

    if (inspectionTasks && existingTaskCount) {
      const existingTaskIds: string[] = inspectionTasks.map(({ taskId }) => taskId);

      exampleImageDetails = existingTaskIds.map((taskId, i) => ({
        id: '',
        taskId,
        path: `path/to/image${i}`,
        imageUrl: `https://example.com/image${i}`,
        blade: '',
        bladeChamber: '',
        bladeSide: '',
        radialDistance: '',
        height: '',
        direction: '',
      }));
    }

    const csvDeleteNote = t(
      'inspection.deleteRowBeforeUploading',
      'DELETE THIS ROW BEFORE UPLOADING'
    );
    const exampleCsv =
      'Notes,' +
      internalFields.map(field => field.defaultCsvHeader ?? field.internalName).join(',') +
      '\n' +
      `${t('inspection.csvExplanationLabel', 'Explanation: {{deleteNote}}', {
        deleteNote: csvDeleteNote,
      })},` +
      internalFields.map(field => `"${field.explanation?.replace(/\"/g, '""') ?? ''}"`).join(',') +
      '\n' +
      `${t('inspection.csvValidDataOptionsLabel', 'Valid Data Options: {{deleteNote}}', {
        deleteNote: csvDeleteNote,
      })},` +
      internalFields.map(field => `"${field.validOptions?.replace(/\"/g, '""') ?? ''}"`).join(',') +
      '\n' +
      exampleImageDetails
        .map(
          (details, i) =>
            `${t('inspection.csvExampleLabel', 'Example {{number}}: {{deleteNote}}', {
              number: i + 1,
              deleteNote: csvDeleteNote,
            })},` +
            internalFields
              .map(field => details[field.internalName as keyof typeof details])
              .join(',')
        )
        .join('\n');

    const jsonDeleteNote = t(
      'inspection.deleteEntryBeforeUploading',
      'DELETE THIS ENTRY BEFORE UPLOADING'
    );
    const exampleData = [
      Object.fromEntries([
        [
          'Notes',
          t(
            'inspection.jsonExplanationAndValidDataOptionsLabel',
            'Explanation and Valid Data Options: {{deleteNote}}',
            {
              deleteNote: jsonDeleteNote,
            }
          ),
        ],
        ...internalFields.map(field => [
          field.defaultJsonFieldName ?? field.internalName,
          `${field.explanation ?? ''}${field.validOptions ? ` ${field.validOptions}` : ''}`,
        ]),
      ]),
      ...exampleImageDetails.map((details, i) =>
        Object.fromEntries([
          [
            'Notes',
            t('inspection.jsonExampleLabel', 'Example {{number}}: {{deleteNote}}', {
              number: i + 1,
              deleteNote: jsonDeleteNote,
            }),
          ],
          ...internalFields.map(field => [
            field.defaultJsonFieldName ?? field.internalName,
            details[field.internalName as keyof typeof details],
          ]),
        ])
      ),
    ];
    const exampleJson = `data:application/json,${JSON.stringify(exampleData)}`;

    return { exampleCsv, exampleJson };
  }, [internalFields, inspectionTasks, existingTaskCount, t]);

  const parseFile = useCallback(
    async (file: File) => {
      if (file.type === 'text/csv') {
        const csvText = await file.text();

        const { meta, data, delimiter, likelyDelimiterError, error } = parseCsv(
          csvText,
          cellDelimiter
        );

        if (error) {
          setAlertState({
            alertDescription: error,
            alertType: 'error',
          });
        } else {
          setAlertState(null);
          setProvidedFieldNames(meta.fields.filter(field => field !== 'Notes'));
          setFileType(AtlasGqlFileFormat.Csv);
          setCellDelimiter(delimiter);
          setLikelyDelimiterError(likelyDelimiterError);
          setPreviewData(data);
        }
      } else if (file.type === 'application/json') {
        const jsonText = await file.text();

        try {
          const parsedJson = flatParseJSON(jsonText);
          /**
           * This assumes (for now) that all records are the same format and are
           * simple key/value pairs. Based on the requirements, this seems fine
           * for now (especially since we have an example file). We may want to support
           * more options in the future.
           */
          if (isEmpty(parsedJson)) {
            setAlertState({
              alertDescription: t(
                'inspection.fileContainedNoData',
                'File contained no data. Please check your file and reupload.'
              ),
              alertType: 'error',
            });
          } else if (Array.isArray(parsedJson) && parsedJson.length) {
            setProvidedFieldNames(getProvidedFieldNamesFromJsonRecords(parsedJson));
            setFileType(AtlasGqlFileFormat.Json);
            setPreviewData(parsedJson);
          }
        } catch (error) {
          message.error(
            t(
              'inspection.errorParsingJson',
              'Error parsing JSON. Please verify formatting and try again.'
            )
          );
        }
      } else {
        message.error(
          t(
            'inspection.fileTypeNotSupported',
            'File type not supported. Please upload a .csv or .json file.'
          )
        );
      }
    },
    [cellDelimiter, t]
  );

  const handleUpload = ({ file }: { file: File; fileList: File[] }) => {
    setFile(file);
    parseFile(file);
  };

  useEffect(() => {
    if (file) {
      parseFile(file);
    }
  }, [cellDelimiter]);

  useEffect(() => {
    const inspectionType = {
      [AtlasGqlFileFormat.Csv]: CSV_BULK_UPLOAD_OPTIONS.CSV_BULK,
      [AtlasGqlFileFormat.Json]: JSON_BULK_UPLOAD_OPTIONS.JSON_BULK,
    }[fileType];

    setInspectionUploadEventTypes(() => getBulkUploadEventTypes(inspectionType));
  }, [fileType]);

  useEffect(() => {
    if (providedFieldNames?.length && step !== Steps.DetailsPreview) {
      setStep(Steps.DetailsMapping);
    }
  }, [providedFieldNames]);

  useEffect(() => {
    const pathFieldName = fieldMappings?.find(
      ({ targetFieldName }) => targetFieldName === 'path'
    )?.sourceFieldName;

    setImagePathFieldName(pathFieldName ?? 'Path');
  }, [fieldMappings]);

  const { getIngestUploadUrls } = useGetIngestUploadUrls();
  const [validateImageMetadataFile] = useValidateImageMetadataFileMutation();
  const { setSelectedFiles, setUploading } = useUploadContext();
  const { shouldAutoRelease } = useUploadInspectionDataModalContext();

  const handleValidate = async (allImagesHaveUrls: boolean) => {
    if (file) {
      setUploading(true);

      const dataType = inspectionUploadEventTypes.inspectionType;
      const fileName = file.name;

      // Get signed url for upload
      try {
        const { items } = await getIngestUploadUrls({
          dataType,
          files: [{ fileName }],
          prefix: dataType,
          recordId: uploadId,
        });

        const [uploadItem] = items;
        if (!uploadItem) {
          message.error(
            t(
              'inspection.errorUploadingImageDetailsNoUrl',
              'Error uploading image details: Upload URL could not be obtained'
            )
          );
          setUploading(false);
          return;
        }

        setSelectedFiles([
          {
            name: fileName,
            path: '',
            percent: 0,
            sourceName: fileName,
            type: 'file',
            valid: true,
          },
        ]);

        // Upload file to S3
        await uploadIngestFiles({
          items: [{ ...uploadItem, file }],
        });

        // Once the file is in S3, call mutation to process
        const input: AtlasGqlValidateImageMetadataFileInput = {
          uploadId,
          ...(uploadType === UploadTypes.Single ? { inspectionId: inspectionIds[0] } : {}),
          key: uploadItem.fields.key,
          bucket: uploadItem.fields.bucket,
          fileFormat: fileType,
          decimalSeparator: decimalDelimiter,
          delimiter: cellDelimiter,
          fieldMappings,
        };

        // Set the input object in parent state to use for submitting the process mutation
        validateImageMetadataInputRef.current = input;

        // Validate the image metadata
        const { data } = await validateImageMetadataFile({
          variables: { input },
        });

        setSelectedFiles([]);

        if (data?.validateImageMetadataFile?.failureCount) {
          setUploading(false);
          setValidationStepProps({
            validateImageMetadataFile: data?.validateImageMetadataFile,
            fileName: file?.name ?? t('inspection.yourDetailsFile', 'your details file'),
            setStep,
            setErroredUploadSteps,
          });
          setStep(Steps.DetailsValidation);
          setErroredUploadSteps(uploadErrors => ({
            ...uploadErrors,
            imageDetails: true,
          }));
        } else {
          if (allImagesHaveUrls) {
            // We don't have to upload images, because they all have URLs.
            // So call the onProcessComplete callback with empty input instead
            onProcessComplete({});
          } else {
            setUploading(false);
            setStep(Steps.ImageUpload);
          }
        }
      } catch (error) {
        message.error(
          t('inspection.errorUploadingImageDetails', 'Error uploading image details: {{message}}', {
            message: error.message,
          })
        );
        setUploading(false);
        return;
      }
    }
  };

  const { CSVDownloader } = useCSVDownloader();
  // Declaring a styled component inside component because CSVDownloader must be desctructured from useCSVDownloader
  const StyledCSVDownloader = styled(CSVDownloader)<{ $marginRight?: string }>`
    background: transparent;
    border: 0;

    ${({ $marginRight }) => $marginRight && `margin-right: ${$marginRight}`}
  `;

  const handleBack = () => {
    setStep(
      uploadType === UploadTypes.Multiple ? Steps.SummaryValidation : Steps.SingleUploadValidation
    );
  };

  const shouldShowSkyCrawler =
    hasSkyCrawlerUploadRT &&
    hasSkyCrawlerUploadFlag &&
    inspectionIds.length === 1 &&
    [BLADE_INTERNAL].includes(inspectionType?.name ?? '');
  const shouldShowAeroEnterprise =
    hasAeroEnterpriseUploadRT &&
    hasAeroEnterpriseUploadFlag &&
    inspectionIds.length === 1 &&
    [AUTONOMOUS_DRONE, MANUAL_DRONE].includes(inspectionType?.name ?? '');

  const shouldShowRobotics = hasRoboticsUploadFlag && inspectionIds.length === 1;
  const shouldShowSkydio = shouldShowSkydioImport(canUseSkydioImport, inspectionIds);
  const defaultCreatePanel = (
    <div>
      {[Steps.DetailsUpload, Steps.DetailsMapping].includes(step) && (
        <>
          {existingTaskCount ? (
            <p>
              <strong>
                {t(
                  'inspection.uploadDetailsForInspections',
                  'Upload details for {{count}} Inspections',
                  { count: existingTaskCount }
                )}
              </strong>
            </p>
          ) : null}
          <p>
            {t(
              'inspection.photoDetailsFileDescription',
              'The photo details file provides metadata mappings for images that will be uploaded to an inspection.'
            )}
          </p>
          <p>
            {t('inspection.clickToDownloadSample', 'Click to download a sample')}{' '}
            <a href="/json/upload_photos_sample.json" download>
              JSON
            </a>{' '}
            {t('inspection.or', 'or')}{' '}
            <a href="/csv/upload_photos_sample.csv" download>
              CSV
            </a>{' '}
            {t('inspection.or', 'or')}{' '}
            <a href="/xlsx/upload_photos_sample.xlsx" download>
              XLSX
            </a>{' '}
            {t(
              'inspection.sampleFileInstructions',
              'file with explanations and examples. You may use it as a template after deleting the existing entries. If you use the XLSX file, remember to convert it to CSV after completing entries.'
            )}
          </p>
          <p>
            {t(
              'inspection.uploadImagesViaUrlNote',
              'Note: SkySpecs recommends uploading images via URL for file transfers greater than 4GB for better speed and stability.'
            )}
          </p>
        </>
      )}
      {step === Steps.DetailsUpload && (
        <>
          <Upload
            accept={'.csv,.json'}
            beforeUpload={() => false} // bypass antd callbacks; handle change ourselves
            // @ts-ignore - tedious antd types; this is fine
            onChange={handleUpload}
          >
            <Button className="testid-upload-image-details" icon={<UploadOutlined />}>
              {t('inspection.uploadDetails', 'Upload Details')}
            </Button>
          </Upload>
          <br />
          {existingTaskCount ? (
            <>
              <p>
                {t(
                  'inspection.generateImageDetailsFileNote',
                  'If you do not have an image details file for the images from Inspections related to these Tasks, you can generate one to edit that is pre-populated with Task Ids using one of the options below.'
                )}
                <br />
                <br />
                <StyledCSVDownloader
                  data={exampleCsv}
                  filename={'details'}
                  type={'link'}
                  $marginRight={'0.5rem'}
                >
                  <Button icon={<DownloadOutlined />}>
                    {t('inspection.downloadDetailsCsv', 'details.csv')}
                  </Button>
                </StyledCSVDownloader>
                <Button icon={<DownloadOutlined />} download="details.json" href={exampleJson}>
                  {t('inspection.downloadDetailsJson', 'details.json')}
                </Button>
              </p>
            </>
          ) : null}
          <ButtonContainer>
            <Button disabled={initialStep === Steps.DetailsUpload} onClick={handleBack}>
              {t('common.back', 'Back')}
            </Button>
            {uploadType === UploadTypes.Single && (
              <Button onClick={() => setStep(Steps.ImageUpload)}>
                {t('inspection.skip', 'Skip')}
              </Button>
            )}
          </ButtonContainer>
        </>
      )}
      {step === Steps.DetailsMapping && (
        <>
          <Divider />
          <h3>
            {t('inspection.selectedFile', 'Selected file:')} <strong>{file?.name}</strong>
          </h3>
          <p>
            {t(
              'inspection.selectSourceColumnForEachAttribute',
              'Select a source column for each Details attribute'
            )}
          </p>
          <FieldMappingForm
            fieldMappings={fieldMappings}
            fileType={fileType}
            ignoreRequired={!shouldAutoRelease}
            internalFields={internalFields}
            requireAtLeastOneOf={
              uploadType === UploadTypes.Multiple ? [['id', 'taskId']] : undefined
            }
            providedFieldNames={providedFieldNames ?? []}
            previousStep={Steps.DetailsUpload}
            nextStep={Steps.DetailsPreview}
            setStep={setStep}
            setFieldMappings={setFieldMappings}
            likelyDelimiterError={likelyDelimiterError}
            cellDelimiter={cellDelimiter ?? AtlasGqlFileFieldDelimiter.Comma}
            setCellDelimiter={setCellDelimiter}
            decimalDelimiter={decimalDelimiter}
            setDecimalDelimiter={setDecimalDelimiter}
          />
        </>
      )}
      {step === Steps.DetailsPreview && (
        <Preview
          uploadType={uploadType}
          inspectionIds={inspectionIds}
          fieldMappings={fieldMappings}
          previewData={previewData}
          setStep={setStep}
          handleComplete={handleValidate}
        />
      )}
      {step === Steps.DetailsValidation && validationStepProps && (
        <Validation {...validationStepProps} />
      )}
    </div>
  );

  const createPanels: { title: string; content: JSX.Element }[] = [
    {
      title: t('inspection.imageDetailUpload', 'Image Detail Upload'),
      content: defaultCreatePanel,
    },
    ...(shouldShowSkyCrawler
      ? [
          {
            title: t('inspection.skyCrawlerUpload', 'SkyCrawler Upload'),
            content: (
              <RoboticsUpload
                inspectionId={inspectionIds[0]}
                acceptedFileType=".mcap"
                outputDataType={IMAGE_AND_METADATA_UPLOAD_OPTIONS.SKYCRAWLER_LOG}
                onComplete={() => {
                  onComplete && onComplete();
                  onCloseModal();
                }}
                onBack={handleBack}
              />
            ),
          },
        ]
      : []),
    ...(shouldShowAeroEnterprise
      ? [
          {
            title: t('inspection.aeroEnterpriseUpload', 'Aero Enterprise Upload'),
            content: (
              <AeroEnterpriseUpload
                inspectionId={inspectionIds[0]}
                onComplete={() => {
                  onComplete && onComplete();
                  onCloseModal();
                }}
                onBack={handleBack}
              />
            ),
          },
        ]
      : []),
    ...(shouldShowRobotics
      ? [
          {
            title: t('inspection.roboticsUpload', 'Robotics Upload'),
            content: (
              <RoboticsUpload
                inspectionId={inspectionIds[0]}
                acceptedFileType=".zip"
                outputDataType={IMAGE_AND_METADATA_UPLOAD_OPTIONS.ROBOTICS_UPLOAD}
                onComplete={() => {
                  onComplete && onComplete();
                  onCloseModal();
                }}
                onBack={handleBack}
              />
            ),
          },
        ]
      : []),
    ...(shouldShowSkydio
      ? [
          {
            title: t('inspection.skydio.importTitle', 'Skydio Import'),
            content: (
              <SkydioImportPanel
                inspectionIds={inspectionIds}
                onComplete={onComplete}
                onCloseModal={onCloseModal}
              />
            ),
          },
        ]
      : []),
  ];

  return createPanels.length > 1 ? (
    <Collapse accordion={true} defaultActiveKey={0}>
      {createPanels.map((panel, index) => {
        const { title, content } = panel;
        return (
          <Collapse.Panel header={title} key={index}>
            {content}
          </Collapse.Panel>
        );
      })}
    </Collapse>
  ) : (
    defaultCreatePanel
  );
};
